import ReportGmailerrorredOutlinedIcon from '@mui/icons-material/ReportGmailerrorredOutlined'
import { Box, Button, Dialog, Stack } from '@mui/material'
import { ReactComponent as NextIcon } from 'assets/arrow_back_logo.svg'
import { ReactComponent as CloseLogo } from 'assets/cross_close_logo.svg'
import {
	IMilkingProcessDeviationEntry,
	deviationLabels,
} from 'models/MilkingProcess'
import moment from 'moment'
import React from 'react'
import { COLORS } from 'styles/colors'
import Preloader from 'ui/Preloader'
import { Scrollbar } from 'ui/scrollbar/Scrollbar'
import { TypographyHeader } from 'ui/typography/TypographyHeader'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographyPrimary14Bold } from 'ui/typography/TypographyPrimary14Bold'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import TooltipHover from 'ui/TooltipHover'
import { MilkingUnitOverlay } from './MilkingUnitOverlay'
// import { VideoPlayer } from 'ui/videoPlayer' // Commented out: switched to Fluesonic embedded player

interface DeviationVideoModalProps {
	open: boolean
	onClose: () => void
	videoUrl: string
	entry?: IMilkingProcessDeviationEntry
	canPrev?: boolean
	canNext?: boolean
	onPrev?: () => void
	onNext?: () => void
	isLoading?: boolean
	onToggleFalseMark?: (entry: IMilkingProcessDeviationEntry) => void
	isFalseMarkUpdating?: boolean
}
const HEIGHT_FRAME = 387
export const DeviationVideoModal: React.FC<DeviationVideoModalProps> = ({
	open,
	onClose,
	videoUrl,
	entry,
	canPrev = true,
	canNext = true,
	onPrev,
	onNext,
	isLoading,
	onToggleFalseMark,
	isFalseMarkUpdating,
}) => {
	const isFalseMarked = Boolean(entry?.is_false)

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			PaperProps={{
				sx: {
					borderRadius: '12px',
					width: { xs: 'calc(100vw - 32px)', sm: 860 },
					height: 'auto',
					maxHeight: 'calc(100vh - 32px)',
					m: { xs: 2, sm: 0 },
					overflow: 'hidden',
					display: 'flex',
					flexDirection: 'column',
					boxSizing: 'border-box',
				},
			}}
		>
			<Box
				sx={{
					p: '24px',
					display: 'flex',
					flexDirection: 'column',
					height: { xs: 'auto', sm: '100%' },
				}}
			>
				<Box
					display='flex'
					justifyContent='space-between'
					alignItems='center'
					mb={'16px'}
				>
					<TypographyHeader>Видеофиксация отклонения</TypographyHeader>

					<CloseLogo
						onClick={onClose}
						stroke={COLORS.secondary}
						style={{ cursor: 'pointer' }}
					/>
				</Box>
				{isLoading ? (
					<Preloader />
				) : (
					<Scrollbar
						sx={{
							flex: 1,
							minHeight: 0,
							scrollbarWidth: 'none',
							msOverflowStyle: 'none',
							'&::-webkit-scrollbar': {
								display: 'none',
							},
						}}
					>
						<Box>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									mb: '16px',
									gap: '4px',
								}}
							>
								<NextIcon
									onClick={canPrev ? onPrev : undefined}
									style={{
										opacity: canPrev === false ? 0.3 : 1,
										cursor: canPrev === false ? 'default' : 'pointer',
									}}
								/>

								<Box
									height={HEIGHT_FRAME}
									sx={{
										position: 'relative',
										flex: 1,
										width: '100%',
									}}
								>
									{/* Commented out: Previous HLS player
									<VideoPlayer url={videoUrl} height={387} />
									*/}
									{videoUrl ? (
										<>
											<iframe
												src={videoUrl}
												height={HEIGHT_FRAME}
												style={{
													width: '100%',
													height: 'inherit',
													borderRadius: '8px',
												}}
												allowFullScreen
												title='Video-player'
											/>
											{entry?.video_overlay && (
												<MilkingUnitOverlay
													overlay={entry.video_overlay}
													events={entry.milking_events}
													deviation={entry.deviation}
												/>
											)}
										</>
									) : (
										<Stack
											direction='column'
											alignItems='center'
											justifyContent='center'
											height='inherit'
										>
											<TypographySecondary>
												Видео недоступно
											</TypographySecondary>
										</Stack>
									)}
								</Box>

								<NextIcon
									onClick={canNext ? onNext : undefined}
									style={{
										transform: 'rotate(180deg)',
										opacity: canNext === false ? 0.3 : 1,
										cursor: canNext === false ? 'default' : 'pointer',
									}}
								/>
							</Box>
							{entry && (
								<Stack gap='16px'>
									<TypographyPrimary14Bold>
										{deviationLabels[entry.deviation.type]}
									</TypographyPrimary14Bold>
									<Box
										sx={{
											display: 'grid',
											gridTemplateColumns: '140px minmax(0, 1fr)',
											columnGap: '16px',
											rowGap: '8px',
											alignItems: 'center',
										}}
									>
										<TypographySecondary>Дата и время</TypographySecondary>
										<TypographyPrimary>
											{moment(entry.timestamp).format('DD.MM.YY HH:mm ')}
										</TypographyPrimary>

										<TypographySecondary>Доильный зал</TypographySecondary>
										<TypographyPrimary>
											{entry.milking_hall ?? '-'}
										</TypographyPrimary>

										<TypographySecondary>Смена</TypographySecondary>
										<TypographyPrimary>
											{{ am: 'Утренняя', pm: 'Дневная', night: 'Ночная' }[
												entry.period
											] ?? '-'}
										</TypographyPrimary>

										<TypographySecondary>Дояр</TypographySecondary>
										<TypographyPrimary>{entry.milker ?? '-'}</TypographyPrimary>

										<TypographySecondary>Доильное место</TypographySecondary>
										<TypographyPrimary>
											{String(entry.milking_unit_num ?? '-')}
										</TypographyPrimary>
									</Box>
									{entry && onToggleFalseMark && (
										<Box sx={{ mb: '-4px' }}>
											<TooltipHover
												title='Если отклонение определено неверно, пометьте его как ложное срабатывание.'
												placement='top-start'
												oneLine
												maxWidth={620}
												disableHoverBackground
											>
												<Button
													variant='outlined'
													disabled={isFalseMarkUpdating}
													startIcon={
														<ReportGmailerrorredOutlinedIcon
															sx={{ fontSize: 16 }}
														/>
													}
													onClick={() => onToggleFalseMark(entry)}
													sx={{
														width: 'fit-content',
														height: '40px',
														borderRadius: '10px',
														borderColor: COLORS.secondary,
														backgroundColor: isFalseMarked
															? COLORS.secondary
															: COLORS.white,
														color: isFalseMarked
															? COLORS.white
															: COLORS.secondary,
														fontSize: '14px',
														fontWeight: 500,
														lineHeight: '16px',
														textTransform: 'none',
														px: '16px',
														'&:hover': {
															borderColor: COLORS.secondary,
															backgroundColor: isFalseMarked
																? COLORS.secondary
																: COLORS.white,
															color: isFalseMarked
																? COLORS.white
																: COLORS.secondary,
															boxShadow: 'none',
														},
														'&:disabled': {
															borderColor: COLORS.secondaryFontOpacity,
															backgroundColor: isFalseMarked
																? COLORS.secondary
																: COLORS.white,
															color: isFalseMarked
																? COLORS.white
																: COLORS.secondary,
															opacity: 0.7,
														},
													}}
												>
													{isFalseMarked
														? 'Отмечено как ложное'
														: 'Отметить как ложное'}
												</Button>
											</TooltipHover>
										</Box>
									)}
								</Stack>
							)}
						</Box>
					</Scrollbar>
				)}
			</Box>
		</Dialog>
	)
}
